import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import {
  Building2,
  Check,
  Clock,
  Copy,
  DoorClosed,
  DoorOpen,
  DollarSign,
  Edit,
  MapPin,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { JobPostBadges } from "@/features/job-post/components/job-post-badges";
import { JobPostProviderSpecialties } from "@/features/job-post/components/job-post-provider-specialties";
import { getRecruiterTrackingUrl } from "@/features/recruiter-tracking/getRecruiterTrackingUrl";
import { useOrganization } from "@/hooks/use-organization";
import { useOrgPath } from "@/hooks/useOrgPath";
import type { RouterOutputs } from "@/trpc";
import { formatFacilityWithType } from "../../utils/format-facility-with-type";
import { formatLocation } from "../../utils/format-location";
import { formatSalary } from "../../utils/format-salary";
import type { JobDashboardActions } from "./use-job-dashboard-actions";

type Job = NonNullable<RouterOutputs["jobPost"]["orgGet"]>;

const COPIED_FEEDBACK_MS = 2000;
const COPIED_TOAST_MS = 3000;
const STATUS_ACTION_CLASS_NAME = "gap-2 px-4";

function MetaItem({
  icon: Icon,
  children,
}: {
  icon: typeof MapPin;
  children: string;
}) {
  return (
    <span className="flex min-w-0 items-start gap-2">
      <Icon
        aria-hidden="true"
        className="mt-1 h-4 w-4 shrink-0 text-slate-500"
      />
      <span className="[overflow-wrap:anywhere]">{children}</span>
    </span>
  );
}

function getPostedLabel(job: Job): string {
  if (!job.publishedAt) {
    return "Draft – not yet published";
  }
  return `Posted ${formatDistanceToNow(new Date(job.publishedAt), { addSuffix: true })}`;
}

function PrimaryJobAction({
  job,
  jobId,
  actions,
}: {
  job: Job;
  jobId: string;
  actions: JobDashboardActions;
}) {
  const { selectedOrgBusinessUnit } = useOrganization();

  if (!job.reviewedAt) {
    return (
      <Button
        variant="outline"
        onClick={() => actions.setConfirmDelete(true)}
        className="gap-2 border-red-200 px-4 text-red-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
      >
        <Trash2 size={16} className="shrink-0" />
        Delete
      </Button>
    );
  }
  if (job.open) {
    return (
      <Button
        variant="outline"
        onClick={() => actions.setConfirmClose(true)}
        className={STATUS_ACTION_CLASS_NAME}
      >
        <DoorClosed size={16} className="shrink-0" />
        Close Job
      </Button>
    );
  }
  return (
    <Button
      onClick={() =>
        actions.setOpenJobMutation.mutate({
          id: jobId,
          organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          open: true,
        })
      }
      disabled={actions.setOpenJobMutation.isPending}
      className={STATUS_ACTION_CLASS_NAME}
    >
      <DoorOpen size={16} className="shrink-0" />
      {actions.setOpenJobMutation.isPending ? "Opening..." : "Re-Open Job"}
    </Button>
  );
}

export function JobDashboardHeader({
  job,
  jobId,
  userId,
  canManageJobPosts,
  actions,
}: {
  job: Job;
  jobId: string;
  userId: string | undefined;
  canManageJobPosts: boolean;
  actions: JobDashboardActions;
}) {
  const orgPath = useOrgPath();
  const [isCopyLinkCopied, setIsCopyLinkCopied] = useState(false);
  const facilityName = formatFacilityWithType({
    facility: job.facility,
    facilityType: job.facilityType,
  });
  const location = formatLocation(job);
  const title = job.title || "Untitled Job";

  const handleCopyTrackingLink = async () => {
    if (!userId) {
      return;
    }
    try {
      await navigator.clipboard.writeText(
        getRecruiterTrackingUrl(jobId, userId),
      );
      setIsCopyLinkCopied(true);
      toast("Link copied", {
        description: "Tracking link copied to clipboard.",
        duration: COPIED_TOAST_MS,
        icon: <Check className="h-4 w-4 text-green-500" />,
      });
      setTimeout(() => setIsCopyLinkCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      toast.error("Failed to copy link to clipboard.");
    }
  };

  return (
    <header className="min-w-0 space-y-4">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link
                to={orgPath("/jobs/post")}
                className="inline-flex min-h-11 items-center rounded-md focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
              >
                Job Posts
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem className="min-w-0">
            <BreadcrumbPage className="[overflow-wrap:anywhere]">
              {title}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold leading-tight tracking-tight text-slate-900 [overflow-wrap:anywhere] sm:text-3xl">
              {title}
            </h1>
            <JobPostBadges job={job} />
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 text-sm leading-6 text-slate-600 sm:flex-row sm:flex-wrap">
            <MetaItem icon={MapPin}>
              {location || "Location not specified"}
            </MetaItem>
            {facilityName && (
              <MetaItem icon={Building2}>{facilityName}</MetaItem>
            )}
            {job.wageFrequencyId && (
              <MetaItem icon={DollarSign}>{formatSalary(job)}</MetaItem>
            )}
            <MetaItem icon={Clock}>{getPostedLabel(job)}</MetaItem>
          </div>
          <JobPostProviderSpecialties
            providerTypes={job.healthcareProfessionalTypes}
            specialties={job.specialties}
          />
        </div>

        <div className="flex flex-wrap gap-2 lg:shrink-0 lg:justify-end">
          {userId && (
            <Button
              variant="outline"
              onClick={handleCopyTrackingLink}
              className="gap-2 px-4"
            >
              {isCopyLinkCopied ? (
                <Check size={16} className="shrink-0 text-green-500" />
              ) : (
                <Copy size={16} className="shrink-0" />
              )}
              Share
            </Button>
          )}
          {canManageJobPosts && (
            <>
              <Button asChild variant="outline" className="gap-2 px-4">
                <Link to={orgPath("/jobs/post/edit/" + jobId)}>
                  <Edit size={16} className="shrink-0" />
                  Edit
                </Link>
              </Button>
              <PrimaryJobAction job={job} jobId={jobId} actions={actions} />
            </>
          )}
        </div>
      </div>
    </header>
  );
}
